import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  hibernateDevice,
  remoteCommands,
  remoteOpsPageQuery,
  restartDevice,
  sendDeviceMessage,
  shutdownDevice,
  wakeDevice,
  type RemoteOpsGroup,
} from '../api/devices'
import { openRemoteConnect } from '../remote_connect/RemoteConnectLaunch'
import { RustdeskInstallButton } from '../remote_connect/RustdeskInstallProgress'
import { CheckField, FormRow, HalfField } from '../ui/FormGrid'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { SettingsCheck } from '../ui/SettingsParts'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Remote Operations - opteemum/devices/details/remoteActions.php.
 * Power / Send message / remoteCommands posteaza aceleasi campuri ca CI3.
 * Remote connect deschide direct pagina de lansare RustDesk
 * (`openRemoteConnect`) - ScreenConnect e scos din uz, iar butonul "test" din
 * macheta veche (`create_device_session/1`) era acelasi buton cu un argument
 * pe care metoda nu-l citea, deci face acelasi lucru.
 *
 * -----------------------------------------------------------------------------
 * FORMULARELE STAU PE GRILA COMUNA (`ui/FormGrid`)
 * -----------------------------------------------------------------------------
 *
 * Cele trei ferestre (Restart / Send message / Shutdown) aveau `form-group`-uri
 * goale, cu eticheta scrisa de mana si campul dedesubt - adica forma pe care
 * fisa unui cont din Settings a lasat-o in urma pe 10 septembrie 2026.
 *
 * Ferestrele astea NU sunt scoase din pagina intr-un portal: `ui/Modal` le
 * deseneaza acolo unde stau, adica INAUNTRUL randului desfasurat al listei, sub
 * `dataTables_wrapper form-inline`. De la 768px in sus, cele doua reguli
 * Bootstrap ale lui `.form-inline` (app.css 2987 si 2992) desfaceau fiecare
 * `.form-group` in `inline-block` si fiecare `.form-control` in `width:auto`,
 * deci cele trei randuri ale ferestrei de Restart se insirau UNUL LANGA ALTUL pe
 * o singura linie: masurat in Chromium la 1600px, campul "Message" iesea 186px
 * intr-o fereastra de 568px, iar lista "Time until restart" ajungea la 945px, in
 * dreapta lui, in loc sa fie pe randul de sub el. `FormRow` poarta
 * `od-form-row`, clasa pe care `v4.css` o scoate din joc inapoi - de-aia trec
 * campurile pe aici.
 *
 * Bifele de fortare sunt tinute acum in stare, nu citite din DOM: ce se trimite
 * ramane acelasi (`restartDevice(id, message, time, force)`).
 */

export function DeviceRemoteOps({ id }: ScreenProps) {
  const page = useQuery(remoteOpsPageQuery(id))
  const query = useQueryClient()
  const [restartOpen, setRestartOpen] = useState(false)
  const [shutdownOpen, setShutdownOpen] = useState(false)
  const [messageOpen, setMessageOpen] = useState(false)
  /* Bifele celor doua ferestre. `CheckField` e un camp condus, iar ce pleaca
     mai departe ramane acelasi `force` pe care il citea `form.elements`. */
  const [forceRestart, setForceRestart] = useState(false)
  const [forceShutdown, setForceShutdown] = useState(false)

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const hasAny = page.data.groups.some((g) => g.commands.length > 0)
  const refresh = () => query.invalidateQueries({ queryKey: ['device', id, 'remote-ops'] })

  return (
    <div className="device-remote-ops">
      {!hasAny && (
        <div className="alert alert-info" style={{ margin: 20 }}>
          <i className="fas fa-info-circle" aria-hidden="true"></i> There are no remote operations for this machine.
        </div>
      )}
      {page.data.groups.map((group) =>
        group.commands.length === 0 ? null : (
          <GroupForm
            key={group.id}
            group={group}
            deviceId={id}
            onSubmit={async (actions, inform) => {
              await remoteCommands(id, group.group_name, group.id, actions, inform)
              toast('Instruction sent.', 'Success')
              await refresh()
            }}
          />
        ),
      )}
      <div className="panel-body">
        <button
          className="btn btn-sm btn-success"
          type="button"
          data-device={id}
          onClick={() => openRemoteConnect({ device: id })}
        >
          <i className="fas fa-desktop"></i> Remote connect
        </button>{' '}
        {/* "On demand": RustDesk se instala numai la cerere - butonul si
            progresul lui. Pe "At start" sau cu RustDesk instalat nu apare. */}
        <RustdeskInstallButton deviceId={id} />
        {page.data.show_test_connect && (
          <>
            {' '}
            <button
              className="btn btn-sm btn-success"
              type="button"
              data-device={id}
              onClick={() => openRemoteConnect({ device: id })}
            >
              <i className="fas fa-desktop"></i> Remote connect test
            </button>
          </>
        )}{' '}
        <button
          className="btn btn-sm btn-primary"
          type="button"
          data-toggle="modal"
          data-target="#sendMessage"
          data-id={id}
          onClick={() => setMessageOpen(true)}
        >
          <i className="fas fa-bullhorn"></i> Send message
        </button>{' '}
        <button
          className="btn btn-sm btn-warning"
          type="button"
          data-toggle="modal"
          data-target="#restartDeviceModal"
          data-id={id}
          data-name={page.data.device.name}
          data-role={page.data.device.role}
          data-os={page.data.device.os}
          onClick={() => setRestartOpen(true)}
        >
          <i className="fas fa-undo"></i> Restart device
        </button>{' '}
        <button
          className="btn btn-sm goHibernate"
          type="button"
          data-id={id}
          data-name={page.data.device.name}
          data-role={page.data.device.role}
          data-os={page.data.device.os}
          onClick={() => {
            void hibernateDevice(id).then(() => toast('Hibernate instruction sent.', 'Success'))
          }}
        >
          <i className="far fa-moon"></i> Hibernate
        </button>{' '}
        <button
          className="btn btn-sm wakeUpDevice"
          type="button"
          data-id={id}
          onClick={() => {
            void wakeDevice(id)
              .then(() => toast('Wake up instruction sent.', 'Success'))
              .catch((err: Error) => toast(err.message, 'Error', 'error'))
          }}
        >
          <i className="far fa-bell"></i> Wake up on lan
        </button>{' '}
        <button
          className="btn btn-sm btn-danger"
          type="button"
          data-toggle="modal"
          data-target="#shutdownDeviceModal"
          data-id={id}
          data-name={page.data.device.name}
          data-role={page.data.device.role}
          data-os={page.data.device.os}
          onClick={() => setShutdownOpen(true)}
        >
          <i className="fas fa-power-off"></i> Shutdown device
        </button>
      </div>

      <Modal id="restartDeviceModal" open={restartOpen} onClose={() => setRestartOpen(false)} title="Restart device">
        <form
          action="/opteemum/restartDevice"
          method="post"
          id="restartDeviceForm"
          onSubmit={(e) => {
            e.preventDefault()
            const form = e.currentTarget
            const message = (form.elements.namedItem('restart_message') as HTMLInputElement).value
            const time = (form.elements.namedItem('restart_time') as HTMLSelectElement).value
            void restartDevice(id, message, time, forceRestart).then(() => {
              toast('Restart instruction sent.', 'Success')
              setRestartOpen(false)
            })
          }}
        >
          <FormRow>
            <HalfField label="Message" htmlFor="restart_message">
              <input
                id="restart_message"
                className="form-control"
                type="text"
                name="restart_message"
                defaultValue="Please save your work. Windows will restart."
              />
              <input value={id} name="deviceId" type="hidden" />
            </HalfField>
            <HalfField label="Time until restart (seconds)" htmlFor="restart_time">
              <select id="restart_time" className="form-control" name="restart_time" defaultValue="5">
                <option value="5">5</option>
                <option value="30">30</option>
                <option value="60">60</option>
                <option value="120">120</option>
              </select>
            </HalfField>
          </FormRow>
          <FormRow>
            <CheckField
              id="force_restart"
              label="Force restart"
              checked={forceRestart}
              onChange={setForceRestart}
              className="iCheckForce"
            />
          </FormRow>
          <div className="modal-footer" style={{ margin: '15px -15px -15px', padding: '15px' }}>
            <button type="button" className="btn btn-default" onClick={() => setRestartOpen(false)}>
              Close
            </button>
            <button type="submit" className="btn btn-success goRestartDevice">
              <i className="fas fa-sync-alt"></i> Restart
            </button>
          </div>
        </form>
      </Modal>

      <Modal id="sendMessage" open={messageOpen} onClose={() => setMessageOpen(false)} title="Send message to this device">
        {/*
          `form-horizontal labelcustomize` + `data-collabel="3"` /
          `data-alignlabel` / `data-label` erau UN SINGUR lucru: eticheta pe o
          coloana din stanga, latimea si alinierea ei citite de javascriptul de
          personalizare al portalului. In v4 nu ruleaza acel javascript, deci
          atributele nu spuneau nimic, iar `form-horizontal` ramanea singura
          asezare din fisa care mai punea eticheta LANGA camp in loc de deasupra
          lui. Randul trece pe grila comuna, cu tot cu eticheta.
        */}
        <form
          id="deviceMessageForm"
          method="post"
          action="/opteemum/sendMessage"
          onSubmit={(e) => {
            e.preventDefault()
            const message = (e.currentTarget.elements.namedItem('message') as HTMLTextAreaElement).value
            void sendDeviceMessage(id, message).then(() => {
              toast('New message sent.', 'Success')
              setMessageOpen(false)
            })
          }}
        >
          <FormRow>
            <HalfField label="Message" required htmlFor="device_message">
              <textarea id="device_message" style={{ maxWidth: '100%' }} className="form-control" name="message" required></textarea>
              <input value={id} name="deviceId" type="hidden" />
            </HalfField>
          </FormRow>
          <div className="modal-footer" style={{ margin: '15px -15px -15px', padding: '15px' }}>
            <button type="button" className="btn btn-default" onClick={() => setMessageOpen(false)}>
              Close
            </button>
            <button type="submit" className="btn btn-primary goSendMessage">
              <i className="far fa-paper-plane"></i> Send message
            </button>
          </div>
        </form>
      </Modal>

      <Modal id="shutdownDeviceModal" open={shutdownOpen} onClose={() => setShutdownOpen(false)} title="Shutdown device">
        <form
          action="/opteemum/shutDownDevice"
          method="post"
          onSubmit={(e) => {
            e.preventDefault()
            const form = e.currentTarget
            const message = (form.elements.namedItem('shutdown_message') as HTMLInputElement).value
            const time = (form.elements.namedItem('shutdown_time') as HTMLSelectElement).value
            void shutdownDevice(id, message, time, forceShutdown).then(() => {
              toast('Shut down instruction sent.', 'Success')
              setShutdownOpen(false)
            })
          }}
        >
          <FormRow>
            <HalfField label="Message" htmlFor="shutdown_message">
              <input
                id="shutdown_message"
                className="form-control"
                type="text"
                name="shutdown_message"
                defaultValue="Please save your work. Windows will shut down."
              />
              <input value={id} name="deviceId" type="hidden" />
            </HalfField>
            <HalfField label="Time until shutdown (seconds)" htmlFor="shutdown_time">
              <select id="shutdown_time" className="form-control" name="shutdown_time" defaultValue="5">
                <option value="5">5</option>
                <option value="30">30</option>
                <option value="60">60</option>
                <option value="120">120</option>
              </select>
            </HalfField>
          </FormRow>
          <FormRow>
            <CheckField id="force_shutdown" label="Force shutdown" checked={forceShutdown} onChange={setForceShutdown} />
          </FormRow>
          <div className="modal-footer" style={{ margin: '15px -15px -15px', padding: '15px' }}>
            <button type="button" className="btn btn-default" onClick={() => setShutdownOpen(false)}>
              Close
            </button>
            <button type="submit" className="btn btn-danger">
              <i className="fas fa-power-off"></i> Shutdown
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

function GroupForm({
  group,
  deviceId,
  onSubmit,
}: {
  group: RemoteOpsGroup
  deviceId: number
  onSubmit(actions: string[], inform: boolean): Promise<void>
}) {
  const columns = splitColumns(group.commands)

  /*
   * LISTA DE COMENZI RAMANE LISTA, si dinadins.
   *
   * Comenzile vin din `it_cmd_commands`, taiate pe masina: un grup are cateva,
   * altul cateva zeci. `CheckField` le-ar pune pe cate o jumatate de rand
   * (`col-md-6`), deci un grup de douazeci de comenzi ar fi de zece randuri in
   * loc de cinci, si s-ar pierde impartirea pe patru coloane pe care o are si
   * portalul. Fiecare comanda e o `SettingsCheck`: bifa in desenul nou si
   * titlul in acelasi `<label>`, deci un clic pe titlu bifeaza si cititorul de
   * ecran stie ce bifeaza. Numele trimise raman `actions[]` si `inform_user`.
   */

  return (
    <>
      <div style={{ padding: '0 20px' }}>
        <form
          method="post"
          className="remoteActionsForm"
          action="/opteemum/remoteCommands"
          onSubmit={(e) => {
            e.preventDefault()
            const form = e.currentTarget
            const actions = [...form.querySelectorAll<HTMLInputElement>('input[name="actions[]"]:checked')].map((el) => el.value)
            const inform = (form.elements.namedItem('inform_user') as HTMLInputElement | null)?.checked ?? false
            void onSubmit(actions, inform)
          }}
        >
          <input type="hidden" name="device" value={deviceId} />
          <input type="hidden" name="commands_group" value={group.group_name} />
          <input type="hidden" name="commands_group_id" value={group.id} />
          <div className="row">
            {columns.map((col, i) => (
              <div className="col-md-3" key={i}>
                <ul>
                  {col.map((cmd, j) => (
                    <li key={`${cmd.command}-${j}`} style={{ marginBottom: 10 }}>
                      <SettingsCheck
                        id={`rops-${group.id}-${i}-${j}`}
                        defaultChecked={cmd.checked}
                        name="actions[]"
                        value={cmd.command}
                        className="check-inform"
                        label={cmd.command_title}
                      />
                      {cmd.info !== '' && (
                        <>
                          {' '}
                          <i
                            className="fas fa-info-circle color-blue"
                            tabIndex={j}
                            data-html="true"
                            data-trigger="hover"
                            role="button"
                            data-toggle="popover"
                            title={cmd.command_title}
                            data-content={cmd.info}
                            aria-hidden="true"
                          ></i>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 20 }}>
            <button className="btn btn-primary btn-sm" type="submit">
              {group.group_name}
            </button>
            <div style={{ display: 'inline-block', verticalAlign: 'top', padding: '5px 15px' }}>
              <SettingsCheck id={`rops-inform-${group.id}`} className="inform_user" name="inform_user" defaultChecked label="Inform user" />
            </div>
          </div>
        </form>
      </div>
      <hr />
    </>
  )
}

function splitColumns<T>(items: T[]): T[][] {
  if (items.length === 0) return []
  const limit = Math.round(items.length / 4)
  const cols: T[][] = [[]]
  let line = 0
  for (const item of items) {
    if (line === limit) {
      line = 0
      cols.push([])
    }
    line += 1
    cols[cols.length - 1].push(item)
  }
  return cols
}
